<template>
  <div>
    <modal
      :titulo="$t('ACADEMICO.FORMULARIO_PROTOCOLO')"
      id="modalMotivoCancelamento"
      :exibir="exibir"
      @fechar="fecharModal"
      :ocultarRodape="true"
    >
      <b-row class="justify-content-end">
        <b-col md="12">
          <input-text-area
            ref="descricao"
            :disabled="!!dados?.id"
            v-model="form.descricao"
            :label="$t('ACADEMICO.SOLICITACAO')"
            :placeholder="$t('ACADEMICO.SOLICITACAO_SOBRE')"
            required
          />
        </b-col>

        <b-col md="12" v-if="dados?.resposta">
          <input-text-area
            :disabled="!!form.resposta"
            v-model="form.resposta"
            :label="$t('ACADEMICO.RESPOSTA')"
            :placeholder="$t('ACADEMICO.RESPOSTA')"
          />
        </b-col>

        <b-col v-if="!dados?.id" md="12">
          <input-file
            :label="$t('PROTOCOLO.SELECIONAR_ANEXO')"
            formatos-aceitos="*"
            :tamanho-em-bytes="10000000"
            @selecionar="selecionarAnexo"
            @erro="handleErroAnexo"
          />
        </b-col>
        <b-col md="12" v-if="imagemAnexo">
          <h6 style="width: 100%">Anexo:</h6>
          <img
            alt="anexo"
            :src="imagemAnexo"
            class="img-fluid cover"
            @click="downloadImagem"
          />
          <b-button
            variant="primary"
            class="w-100 mt-2"
            @click="downloadImagem"
          >
            {{ $t("GERAL.BAIXAR") }}
          </b-button>
        </b-col>

        <b-col class="text-right mt-2" md="auto" v-if="!dados?.id">
          <b-button variant="primary" class="w-100" @click="cadastrar">{{
            $t("ACADEMICO.ABRIR_PROTOCOLO")
          }}</b-button>
        </b-col>
      </b-row>
    </modal>
  </div>
</template>

<script>
//Helpers & Aux
import JwtService from "@/common/utils/jwt";
import { START_LOADING, STOP_LOADING } from "@/store/Store";
import mensagem from "@/common/utils/mensagem";
import helpers from "@/common/utils/helpers";
import { BUSCAR_ALUNO_SELECIONADO } from "@/store/autenticacao.module";

//Components:
import Modal from "@/components/modal/Modal.vue";
import { InputTextArea, InputFile } from "@/components/inputs";

// services
import ProtocoloService from "@/common/services/protocolo/protocolo.service.js";

export default {
  name: "ModalProtocolo",
  components: {
    Modal,
    InputTextArea,
    InputFile,
  },
  props: {
    exibir: { type: Boolean, default: true },
    dados: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        resposta: "",
        descricao: "",
        anexo: "",
        anexoPrefix: "",
        usuarioId: "",
        matriculaId: "",
      },
      imagemAnexo: "",
      imagemNome: "",
      downloadLink: "",
    };
  },
  mounted() {
    this.form.usuarioId = JwtService.buscarId();
    this.buscarMatricula();
  },
  methods: {
    buscarMatricula() {
      this.$store
        .dispatch(BUSCAR_ALUNO_SELECIONADO)
        .then((data) => (this.form.matriculaId = data.matriculaId));
    },

    async buscarAnexo() {
      this.$store.dispatch(START_LOADING);
      try {
        const [{ data }, protResp] = await Promise.all([
          ProtocoloService.buscarAnexo(this.dados.id),
          ProtocoloService.obterTodosPaginado({ id: this.dados.id })
        ]);

        const protocolo = protResp.data?.itens?.find(p => p.id === this.dados.id);
        const anexoPrefix = protocolo?.anexoPrefix || this.dados.anexoPrefix;

        if (anexoPrefix && anexoPrefix.includes('application/octet-stream')) {
          const extensao = this.extractExtensionFromPrefix(anexoPrefix);
          const mimeType = this.getMimeTypeFromExtension(extensao);
          
          this.imagemAnexo = `data:${mimeType};base64,${data.documento}`;
          this.imagemNome = data.nomeArquivo + extensao;
        } else {
          let prefix = (anexoPrefix || '').trim();
          if (prefix && !prefix.toLowerCase().startsWith('data:')) prefix = `data:${prefix}`;
          if (prefix && !/;base64\b/i.test(prefix)) prefix += ';base64';
          prefix = prefix
            .replace(/image\/jpg/i, 'image/jpeg')
            .replace(/image\/pjpeg/i, 'image/jpeg')
            .replace(/image\/jfif/i, 'image/jpeg');

          if (!prefix) {
            const n = (data.nomeArquivo || '').toLowerCase();
            if (n.endsWith('.png')) prefix = 'data:image/png;base64';
            else if (n.endsWith('.pdf')) prefix = 'data:application/pdf;base64';
            else prefix = 'data:image/jpeg;base64';
          }

          this.imagemAnexo = `${prefix},${data.documento}`;

          let nome = (data.nomeArquivo || 'anexo').trim();
          if (!/\.[a-z0-9]{2,5}$/i.test(nome)) {
            if (/image\/jpeg/i.test(prefix)) nome += '.jpg';
            else if (/image\/png/i.test(prefix)) nome += '.png';
            else if (/application\/pdf/i.test(prefix)) nome += '.pdf';
          }
          this.imagemNome = nome.replace(/\.jpeg$/i, '.jpg').replace(/\.jfif$/i, '.jpg');
        }
      } catch (err) {
        mensagem.showErrors(err);
      } finally {
        this.$store.dispatch(STOP_LOADING);
      }
    },

    downloadImagem() {
      const link = document.createElement('a');
      link.href = this.imagemAnexo;
      link.download = this.imagemNome || 'anexo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // FUNÇÕES CRUD:
    cadastrar() {
      if (!this.validarFormulario()) return;

      this.$store.dispatch(START_LOADING);

      ProtocoloService.criar(this.form)
        .then((data) => {
          mensagem.sucesso(this.$t("GERAL.SUCCESSO"), data.mensagem);
          this.fecharModal();
          this.atualizar();
        })
        .catch((error) => {
          mensagem.showErrors(error);
        })
        .finally(() => {
          this.$store.dispatch(STOP_LOADING);
        });
    },

    selecionarAnexo(dadosArquivo) {
      this.form.anexoPrefix = dadosArquivo.prefix;
      this.form.anexo = dadosArquivo.data;
      
      this.imagemAnexo = dadosArquivo.base64Completo;
    },
    
    handleErroAnexo(mensagemErro) {
      mensagem.erro(mensagemErro);
    },

    getFileExtension(filename) {
      if (!filename) return '';
      const lastDot = filename.lastIndexOf('.');
      return lastDot !== -1 ? filename.substring(lastDot).toLowerCase() : '';
    },

    getMimeTypeFromExtension(extensao) {
      const mimeTypes = {
        '.pdf': 'application/pdf',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.bmp': 'image/bmp',
        '.webp': 'image/webp',
        '.svg': 'image/svg+xml',
        '.doc': 'application/msword',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.xls': 'application/vnd.ms-excel',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.ppt': 'application/vnd.ms-powerpoint',
        '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        '.txt': 'text/plain',
        '.rtf': 'application/rtf',
        '.zip': 'application/zip',
        '.rar': 'application/x-rar-compressed',
        '.7z': 'application/x-7z-compressed',
        '.mp3': 'audio/mpeg',
        '.wav': 'audio/wav',
        '.mp4': 'video/mp4',
        '.avi': 'video/x-msvideo',
        '.mov': 'video/quicktime',
        '.csv': 'text/csv',
        '.json': 'application/json',
        '.xml': 'application/xml',
        '.html': 'text/html',
        '.css': 'text/css',
        '.js': 'application/javascript'
      };
      
      return mimeTypes[extensao] || 'application/octet-stream';
    },

    extractExtensionFromPrefix(prefix) {
      const match = prefix.match(/data:application\/octet-stream;base64(\.[^.]+)$/);
      return match ? match[1] : '';
    },

    validarFormulario() {
      return helpers.validarFormulario(this.$refs);
    },
    fecharModal() {
      this.$emit("fechar");
    },
    atualizar() {
      this.$emit("atualizar");
    },
  },
  watch: {
    exibir(exibir) {
      if (exibir) {
        if (this.dados) {
          this.form = { ...this.dados };
          this.buscarAnexo();
        } else {
          this.form = {
            resposta: "",
            descricao: "",
            anexo: "",
            anexoPrefix: "",
            usuarioId: "",
            matriculaId: "",
          };
          this.form.usuarioId = JwtService.buscarId();
          this.buscarMatricula();
          this.imagemAnexo = null;
          this.imagemNome = null;
        }
      }
    },
  },
};
</script>
